<template>
  <PhaseSection
    data-testid="quick-action-review"
    noColumns
    :description="$t('rev_cycle.review.description')"
    :hasNextButton="hasNextButton"
    :hasBackButton="hasBackButton"
    @next="$emit('next')"
    @back="$emit('back')"
  >
    <template>
      <TranslationLoading v-if="isTranslating" />
      <template v-else>
        <ReviewHeader
          :validationRequestErrorText="validationRequestErrorText"
          :rev="rev"
          :showEstimated="showEstimated"
          :isImpactValidated="isImpactValidated"
          :isImplemented="isImplemented"
          :hasRequestValidation="hasRequestValidation"
          :showRequestError="showRequestError"
          :loading="isSaving"
          :canImplement="canImplement"
          :canDoFinancialValidation="canDoFinancialValidation"
          :isCompleted="isCompleted"
          @showEstimated="handleShowEstimated"
          @implementRev="$emit('implementRev')"
          @handleRequestValidationClick="handleRequestValidationClick"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
          @validate="$emit('validate')"
          @closeRequestError="showRequestError = false"
        />

        <ReviewValidation
          class="mb-2"
          :isImpactValidated="isImpactValidated"
          :showValidated="!showEstimated"
          :rev="rev"
          :hasRequestValidation="hasRequestValidation"
          :isRevImplemented="isImplemented"
          :editMode="isEditMode"
          @update="updateReviewField"
        />
        <RequestValidationModal
          :show="showRequestModal"
          @hideModal="showRequestModal = false"
          @createValidationRequest="$emit('createValidationRequest', $event)"
        />
        <RequestViewModal
          :show="showRequestViewModal"
          :requestValidation="requestValidationInProgress"
          @hideModal="showRequestViewModal = false"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
        />
      </template>
    </template>
    <template>
      <template slot="left">
        <img :src="reviewImage" alt="economic-impact-illustration" />
      </template>
      <TranslationLoading slot="right" v-if="isTranslating" />
      <template slot="right" v-else>
        <div class="d-flex mb-4" v-if="showFilters && !isEditMode">
          <v-chip
            class="mr-4"
            data-tourid="filter_validation_estimated"
            :color="
              !validatedFilterIsSelected ? 'primary' : 'blue-grey darken-2'
            "
            :outlined="validatedFilterIsSelected"
            @click="selectFilter(FILTERS.ESTIMATED)"
          >
            {{ $t('rev.validation.estimated') }}
          </v-chip>
          <v-chip
            data-tourid="filter_validation_validated"
            :color="
              validatedFilterIsSelected ? 'primary' : 'blue-grey darken-2'
            "
            :outlined="!validatedFilterIsSelected"
            @click="selectFilter(FILTERS.VALIDATED)"
          >
            <v-icon class="mr-1" size="16">{{ validatedIcon }}</v-icon>
            {{ $t('rev.validation.validated') }}
          </v-chip>
        </div>
        <MoneyQuestion
          v-if="shouldShowMoneyQuestion(costReduction)"
          data-private
          class="mb-4"
          data-testid="quick-action-review-cost"
          :tourId="`quickAction-input-costReduction`"
          :isEditMode="isEditMode"
          :value="costReduction"
          :currency="currency"
          :currencySymbol="currencySymbol"
          :organizationCurrency="organizationCurrency"
          :organizationCurrencySymbol="organizationCurrencySymbol"
          :exchangeRate="exchangeRate"
          :question="$t('quick_action.review.whats_the_cost')"
          :period="$t('rev_cycle.review.month')"
          :isImpactValidated="isImpactValidated"
          @input="updateReviewField('costReduction', $event)"
          @onBlur="trackEvent('costReduction')"
        />
        <EconomicImpactCard
          v-else
          data-private
          class="mb-2"
          :title="$t('quick_action.review.monthly_cost')"
          :imageUrl="costReductionImage"
          :iconUrl="costReductionIcon"
          :currency="currency"
          :currencySymbol="currencySymbol"
          :exchangeCurrency="organizationCurrency"
          :organizationCurrencySymbol="organizationCurrencySymbol"
          :exchange="calculateRate(costReduction)"
          :economicImpact="costReduction"
        />
        <MoneyQuestion
          v-if="shouldShowMoneyQuestion(investment)"
          data-private
          class="mb-4"
          data-testid="quick-action-review-investment"
          :tourId="`quickAction-input-investment`"
          :isEditMode="isEditMode"
          :value="investment"
          :currency="currency"
          :currencySymbol="currencySymbol"
          :organizationCurrency="organizationCurrency"
          :organizationCurrencySymbol="organizationCurrencySymbol"
          :exchangeRate="exchangeRate"
          :question="$t('quick_action.review.investment_to_solve')"
          :isImpactValidated="isImpactValidated"
          @input="updateReviewField('investment', $event)"
          @onBlur="trackEvent('investment')"
        />
        <EconomicImpactCard
          v-else
          data-private
          class="mb-2"
          :title="$t('common.total_investment')"
          :imageUrl="investmentImage"
          :iconUrl="investmentIcon"
          :currency="currency"
          :currencySymbol="currencySymbol"
          :exchangeCurrency="organizationCurrency"
          :organizationCurrencySymbol="organizationCurrencySymbol"
          :exchange="calculateRate(investment)"
          :economicImpact="investment"
        />
        <TimeImpact
          monthly
          :milliseconds="timeReductionByMonth"
          :isEditMode="isEditMode"
          :isImpactValidated="isImpactValidated"
          @update="updateReviewField('timeReductionByMonth', $event)"
        />
        <TimeImpact
          :milliseconds="timeInvested"
          :isEditMode="isEditMode"
          :isImpactValidated="isImpactValidated"
          @update="updateReviewField('timeInvested', $event)"
        />
        <TextQuestion
          data-private
          data-testid="quick-action-review-calculate"
          class="my-4"
          rows="6"
          :tourId="'quickAction-textArea-calculate'"
          :value="estimationExplanation"
          :question="$t('quick_action.review.calculate_the_impact')"
          :maxLength="$constants.quickAction.calculatingImpact"
          :placeholder="
            $t('quick_action.review.calculate_the_impact_placeholder')
          "
          :isEditMode="isEditMode"
          :isImpactValidated="isImpactValidated"
          @input="updateReviewField('estimationExplanation', $event)"
          @focusOut="trackEvent('estimationComment')"
        />
        <CategoriesSelector
          data-private
          data-testid="quick-action-review-benefits"
          tourId="quickAction-button-categories"
          v-if="isEditMode"
          :siteIds="[siteId]"
          v-model="selectedCategories"
        />
        <ChipsList
          v-else
          translationKey="common.categories"
          :items="selectedCategories"
          :title="$t('common.category_other')"
          :isImpactValidated="isImpactValidated"
        />
        <RequestValidationModal
          :show="showRequestModal"
          @hideModal="showRequestModal = false"
          @createValidationRequest="$emit('createValidationRequest', $event)"
        />
        <RequestViewModal
          :show="showRequestViewModal"
          :requestValidation="requestValidationInProgress"
          @hideModal="showRequestViewModal = false"
          @cancelValidationRequest="$emit('cancelValidationRequest', $event)"
        />
      </template>
    </template>
  </PhaseSection>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'

import currencies from '@/locales/currencies.json'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin'

import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import MoneyQuestion from '@/components/Rev/Review/MoneyQuestion.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import EconomicImpactCard from '@/components/Rev/Review/EconomicImpactCard.vue'
import TimeImpact from '@/components/Rev/Review/TimeImpact/TimeImpact.vue'
import ChipsList from '@/components/ChipsList.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'

import costReductionImage from '@/assets/rev/cost_reduction.svg'
import costReductionIcon from '@/assets/rev/icon_cost_reduction.svg'
import investmentImage from '@/assets/rev/investment.svg'
import investmentIcon from '@/assets/rev/icon_investment.svg'
import reviewImage from '@/assets/rev/review_illustration.svg'
import RequestValidationModal from '@/containers/Modals/RequestValidationModal.vue'
import RequestViewModal from '@/containers/Modals/RequestViewModal.vue'
import ReviewValidation from '@/components/Rev/Review/ReviewValidation.vue'
import ReviewHeader from '@/components/Rev/Review/ReviewHeader.vue'

const FILTERS = {
  ESTIMATED: 'Estimated',
  VALIDATED: 'Validated',
}
export default {
  name: 'QuickActionReviewStep',

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    rev: {
      type: Object,
      required: true,
    },
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    hasRequestValidation: Boolean,
    canImplement: Boolean,
    isSaving: Boolean,
    canDoFinancialValidation: Boolean,
  },

  mixins: [trackerMixin, mixin],

  components: {
    PhaseSection,
    MoneyQuestion,
    TextQuestion,
    CategoriesSelector,
    EconomicImpactCard,
    TimeImpact,
    TranslationLoading,
    ChipsList,
    RequestValidationModal,
    RequestViewModal,
    ReviewValidation,
    ReviewHeader,
  },

  data() {
    return {
      costReductionImage,
      costReductionIcon,
      investmentImage,
      investmentIcon,
      reviewImage,
      showRequestModal: false,
      showRequestViewModal: false,
      FILTERS,
      selectedFilter: FILTERS.ESTIMATED,
      showEstimated: true,
      showRequestError: false,
      validationRequestErrorText: '',
    }
  },

  mounted() {
    this.selectedFilter = this.showFilters
      ? FILTERS.VALIDATED
      : FILTERS.ESTIMATED
    this.showEstimated = !this.isImpactValidated
  },

  computed: {
    showFilters() {
      return this.isImpactValidated
    },

    validatedFilterIsSelected() {
      return Boolean(this.selectedFilter === FILTERS.VALIDATED)
    },

    validatedIcon() {
      return this.validatedFilterIsSelected
        ? 'mdi-check-decagram'
        : 'mdi-check-decagram-outline'
    },

    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.updateReviewField('categories', categories)
      },
    },

    currency() {
      return this.rev?.currencyMetadata?.currentCurrency
    },

    organizationCurrency() {
      return this.rev?.currencyMetadata?.globalCurrency
    },

    exchangeRate() {
      const exchangeRate = this.rev?.currencyMetadata?.exchangeRate || '0'
      return Number(exchangeRate)
    },

    currencySymbol() {
      const currencyData = currencies[this.currency]
      return currencyData.symbol_native
    },

    organizationCurrencySymbol() {
      const currencyData = currencies[this.organizationCurrency]
      return currencyData.symbol_native
    },

    costReduction() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.costReduction?.capturedValue
        : this.rev?.reviewPhase?.costReduction?.capturedValue

      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    investment() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.investment?.capturedValue
        : this.rev?.reviewPhase?.investment?.capturedValue
      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    timeInvested() {
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.timeTracking?.timeInvested
        : this.rev?.reviewPhase?.timeTracking?.timeInvested
      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    timeReductionByMonth() {
      if (!this.rev?.validatedReview?.timeTracking) return null
      const value = this.validatedFilterIsSelected
        ? this.rev?.validatedReview?.timeTracking.timeReductionByMonth
        : this.rev?.reviewPhase?.timeTracking?.timeReductionByMonth
      if (!_.isString(value) && !_.isNumber(value)) return null
      return Number(value)
    },

    estimationExplanation() {
      if (this.validatedFilterIsSelected)
        return this.rev?.validatedReview?.estimationExplanation
      return this.rev?.reviewPhase?.estimationExplanation
    },

    siteId() {
      return this.rev.location?.site?._id || this.rev.location?.site
    },

    isImplemented() {
      return Boolean(this.rev?.hasImplementationValidation)
    },

    isImpactValidated() {
      return Boolean(this.rev?.hasFinancialValidation)
    },

    requestValidationInProgress() {
      return this.rev?.validationRequest?.find(
        validationRequest =>
          validationRequest?.status ===
          this.$constants.validationRequestStatus.inProgress
      )
    },

    isCompleted() {
      return this.rev.status === this.$constants.revStatus.completed
    },
  },

  methods: {
    ...mapMutations({
      updateCostReduction: 'quickAction/updateCostReduction',
      updateInvestment: 'quickAction/updateInvestment',
      updateEstimationExplanation: 'quickAction/updateEstimationExplanation',
      updateCategories: 'quickAction/updateCategories',
      updateTimeInvested: 'quickAction/updateTimeInvested',
      updateTimeReductionByMonth: 'quickAction/updateTimeReductionByMonth',
      updateImpactMetrics: 'quickAction/updateImpactMetrics',
      updateInvestments: 'quickAction/updateInvestments',
      updateStartDate: 'quickAction/updateStartDate',
      updateEndDate: 'quickAction/updateEndDate',
      setImplementError: 'setImplementError',
    }),

    handleRequestValidationClick() {
      if (!this.isCompleted) {
        this.validationRequestErrorText = this.$t(
          'rev.validation.mark_as_completed_error'
        )
        this.showRequestError = true
        return
      }

      if (!this.isImplemented) {
        this.validationRequestErrorText = this.$t(
          'common.button.mark_this_rev_as_implemented_to_request_validation'
        )
        this.showRequestError = true
        this.setImplementError(true)
        return
      }
      this.trackValidationRequest({
        action: this.hasRequestValidation ? 'view' : 'request',
        type: 'buttonClick',
      })
      this.showRequestError = false
      if (this.hasRequestValidation) {
        this.showRequestViewModal = true
        return
      }
      this.showRequestModal = true
    },

    shouldShowMoneyQuestion(value) {
      return this.isEditMode || _.isNil(value)
    },

    updateReviewField(field, value) {
      this.$emit('revChange')
      switch (field) {
        case 'costReduction':
          return this.updateCostReduction(value)
        case 'investment':
          return this.updateInvestment(value)
        case 'estimationExplanation':
          return this.updateEstimationExplanation(value)
        case 'categories':
          this.trackUpdate(this.rev, 'changeReviewCategories')
          return this.updateCategories(value)
        case 'timeInvested':
          return this.updateTimeInvested(value)
        case 'timeReductionByMonth':
          return this.updateTimeReductionByMonth(value)
        case 'impactMetrics':
          return this.updateImpactMetrics(value)
        case 'investments':
          return this.updateInvestments(value)
        case 'startDate':
          return this.updateStartDate(value)
        case 'endDate':
          return this.updateEndDate(value)
      }
    },

    calculateRate(value) {
      return Math.round(parseInt(value) / this.exchangeRate)
    },

    trackEvent: function (changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    selectFilter(filter) {
      this.trackClickReviewValidationFilter({ type: filter })
      this.selectedFilter = filter
    },

    handleShowEstimated() {
      this.showEstimated = !this.showEstimated
    },
  },

  watch: {
    isCompleted(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
    isImplemented(newVal) {
      if (newVal) {
        this.showRequestError = false
      }
    },
  },
}
</script>
